import React from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Select } from "shared/ui/input/select/select/Select"
import { TextInput } from "shared/ui/input/text/TextInput"

import "../styles/_create-team-member-form.scss"
import { CreateTeamMemberFormViewModel } from "../types/CreateTeamMemberFormView"

export const CreateTeamMemberFormView = React.memo(
  ({
    id,
    roleOptions,
    form,
    fieldArray,
    autoFocus = false,
    className,
    inviteMoreButtonText: customInviteMoreButtonText,
    onInviteMore,
    onSubmit
  }: CreateTeamMemberFormViewModel) => {
    const { t } = useTranslation()

    const inviteMoreButtonText =
      customInviteMoreButtonText || t("Team.Member.InviteMore")

    return (
      <FormProvider {...form}>
        <form
          id={id}
          className={classNames("create-team-member-form", className)}
          onSubmit={onSubmit}
        >
          <ul className="create-team-member-form__field-list">
            {fieldArray.fields.map((field, index) => (
              <li key={field.id} className="create-team-member-form__row">
                <TextInput.Root
                  className="create-team-member-form__email"
                  name={`teamMembers.${index}.email`}
                  label={t("General.Email")}
                  autoFocus={autoFocus && index === 0}
                >
                  <TextInput.Focus>
                    <Icon
                      className="create-team-member-form__user-plus-icon"
                      name={IconName.USER_PLUS}
                    />

                    <TextInput.Input
                      data-testid={`create-team-member-form__email-input__${index}`}
                      type="text"
                      placeholder={t("General.EnterEmailAddress")}
                    />
                  </TextInput.Focus>
                  <TextInput.Readonly />
                  <TextInput.ErrorMessage />
                </TextInput.Root>

                <Select.Root
                  className="create-team-member-form__role"
                  name={`teamMembers.${index}.role`}
                  label={t("Team.Role.Role")}
                  data-testid={`create-team-member-form__role-input__${index}`}
                >
                  <Select.Input
                    data-testid={`create-team-member-form__role-input__${index}`}
                    options={roleOptions}
                    className="create-team-member-form__role-select"
                  />
                  <Select.Readonly />
                </Select.Root>
              </li>
            ))}
          </ul>

          {onInviteMore ? (
            <Button
              variant={ButtonVariant.TERTIARY}
              className="create-team-member-form__invite-more"
              onClick={onInviteMore}
              disabled={form.formState.isSubmitting}
            >
              {inviteMoreButtonText}
            </Button>
          ) : null}
        </form>
      </FormProvider>
    )
  }
)
